import queryString from 'query-string'

import { ERROR_CODES } from '@app/constants/Misc'

import { isAbortError } from '@app/errors/AbortError'

import { createCookieName } from '@app/utils/cookieNames'
import { createCookie } from '@app/utils/cookies'
import { createSelector } from '@app/utils/createSelector'
import moment from '@app/utils/moment'

import { isApiError, isNetworkFailError } from '@app/packages/api/errors'

import { getCookies } from '@app/store/actions/contexts/cookies'
import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

import { profileUserSelector } from './profile'

const pendingPromocodeSlice = createReduxSlice<string>('pending_promocode')
const appliedPromocodeSlice = createReduxSlice<string>('applied_promocode')

/** The code of a /promocode?code= link, kept until it is used: applied to a parent's account, or sent with a sitter's training request. */
export const pendingPromocodeSelector = createSelector([pendingPromocodeSlice.selector], code => code ?? null)

/** The pending code once a parent can take it: signed in, registration finished. */
export const pendingPromocodeToApplySelector = createSelector([pendingPromocodeSelector, profileUserSelector], (code, user) =>
  code && user?.account_type === 'parent' && user.registration_completed ? code : null
)

/** The pending code of an approved sitter, who is past the training request a sitter's code travels with. */
export const pendingPromocodeTooLateSelector = createSelector([pendingPromocodeSelector, profileUserSelector], (code, user) =>
  code && user?.account_type === 'sitter' && user.approved ? code : null
)

/** A pending code that was applied, for the banner, until the parent opens their promocodes or closes it. */
export const appliedPromocodeSelector = createSelector([appliedPromocodeSlice.selector], code => code ?? null)

/** On the server: a /promocode?code= link sets the pending cookie, and every render brings both cookies' codes into the store. */
export const restorePromocodeCookies = (location: { pathname: string; search?: string }) =>
  createThunk(async dispatch => {
    const cookies = dispatch(getCookies())
    const linked = location.pathname === '/promocode' ? queryString.parse(location.search ?? '').code : null
    const fromLink = typeof linked === 'string' ? linked.trim() : ''
    if (fromLink) await pendingCookie.set(cookies, fromLink)
    const pending = fromLink || (await pendingCookie.get(cookies))
    if (pending) dispatch(pendingPromocodeSlice.set(pending))

    const applied = await appliedCookie.get(cookies)
    if (applied) dispatch(appliedPromocodeSlice.set(applied))
  })

/** Applies a parent's pending promocode. One that does not apply to them is dropped silently; they can still try it on /promocode and read why. */
export const applyPendingPromocode = (code: string) =>
  createThunk(async dispatch => {
    const { applyPromocode } = await import('@app/store/actions/payment')
    try {
      await dispatch(applyPromocode(code))
    } catch (e) {
      if (isPromocodeRefusal(e)) {
        await dispatch(clearPendingPromocode())
        return
      }
      // No answer yet: the code stays pending for the next load.
      if (isNetworkFailError(e) || isAbortError(e)) return
      // Anything else keeps it pending too, and reaches Sentry.
      throw e
    }
    await dispatch(markPendingPromocodeApplied(code))
  })

/** The pending code is used up, and the banner says it was applied, through reloads, until dismissed. */
const markPendingPromocodeApplied = (code: string) =>
  createThunk(async dispatch => {
    const cookies = dispatch(getCookies())
    dispatch(pendingPromocodeSlice.remove())
    dispatch(appliedPromocodeSlice.set(code))
    await Promise.all([pendingCookie.rm(cookies), appliedCookie.set(cookies, code)])
  })

/** Forgets the pending code: sent with a training request, or one that does not apply to this account. */
export const clearPendingPromocode = () =>
  createThunk(async dispatch => {
    dispatch(pendingPromocodeSlice.remove())
    await pendingCookie.rm(dispatch(getCookies()))
  })

export const dismissAppliedPromocode = () =>
  createThunk(async dispatch => {
    dispatch(appliedPromocodeSlice.remove())
    await appliedCookie.rm(dispatch(getCookies()))
  })

/** promo_usages refuses a code with VALIDATION_ERROR (expired, used, for new users only…) or PROMOCODE_ERROR (the apply step itself). */
const isPromocodeRefusal = (e: unknown) =>
  isApiError(e) &&
  !!e.response?.errors?.some((item: { code: string }) => item.code === ERROR_CODES.VALIDATION_ERROR || item.code === ERROR_CODES.PROMOCODE_ERROR)

// Both are written by the server and httpOnly: the page reads the codes from the store, never from document.cookie.
const cookieOptions = () => ({ expires: moment().add(30, 'days'), httpOnly: true, sameSite: 'lax' as const, ignoreConsent: true })
const pendingCookie = createCookie<string>(
  createCookieName('kidsout__pending_promocode'),
  v => v,
  v => v,
  cookieOptions
)
const appliedCookie = createCookie<string>(
  createCookieName('kidsout__promocode_notification'),
  v => v,
  v => v,
  cookieOptions
)
